import {
  Box,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import { merchantApi } from "../../../redux/api/apiConfig";
import { KYC_DEVICES } from "../../../ApiUrl";
import { toast } from "react-toastify";
import KycReportTable from "./kycReportTable";
import usePageTitle from "../../../hooks/usePageTitle";
import {
  ButtonResetFilter,
} from "../../../components";
import { FilterDropdown } from "../components";

const KycReport = ({ client }) => {
  usePageTitle("KYC Report", "View KYC Report");
  const [kycData, setKycData] = useState({});
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    email: "",
    ip_address: "",
    device_type: null,
    status: null,
  });
  const [paginationFilter, setPaginationFilter] = useState({
    currentPage: 0,
    pageSize: 7,
  });

  // Device type options
  const deviceTypeOptions = [
    { key: "mobile", name: "Mobile" },
    { key: "android", name: "Android" },
    { key: "iphone", name: "iPhone" },
    { key: "tablet", name: "Tablet" },
    { key: "ipad", name: "iPad" },
    { key: "pc", name: "PC" },
  ];

  // Status options based on API response
  const statusOptions = [
    { key: "user_signed_up", name: "User Signed Up" },
    { key: "buckzy_upload_done", name: "Buckzy Upload Done" },
    { key: "document_upload_done", name: "Document Upload Done" },
    { key: "final_submittion_completed", name: "Final Submission Completed" },
  ];

  async function getKycData(filters, otherFilters) {
    let params = {
      page: filters?.currentPage + 1, // API uses 1-based page numbering
      size: filters?.pageSize || 7, // Page size for pagination
      status: otherFilters?.status?.key || "",
      device_type: otherFilters?.device_type?.key || "",
      email: otherFilters?.email || "",
      ip_address: otherFilters?.ip_address || "",
    };

    // Remove empty params
    Object.keys(params).forEach((key) => {
      if (params[key] === "" || params[key] === null) {
        delete params[key];
      }
    });

    setLoading(true);
    merchantApi
      .get(`${KYC_DEVICES}`, {
        params,
      })
      .then((response) => {
        if (response?.data) {
          setLoading(false);
          setKycData(response?.data);
        }
      })
      .catch((error) => {
        setLoading(false);
        toast.error(
          error?.response?.data?.message || "Error getting KYC report data"
        );
      });
  }

  // Debounce for text inputs
  const debounceTimeoutRef = useRef(null);

  useEffect(() => {
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout for text inputs (email, ip_address)
    if (filters.email || filters.ip_address) {
      debounceTimeoutRef.current = setTimeout(() => {
        getKycData(paginationFilter, filters);
      }, 500);
    } else {
      // For dropdowns, call immediately
      getKycData(paginationFilter, filters);
    }

    // Cleanup
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [paginationFilter, filters]);

  const handleClearFilters = () => {
    setFilters({
      email: "",
      ip_address: "",
      device_type: null,
      status: null,
    });
  };


  return (
    <Box sx={{ padding: "0 24px 24px" }}>
      <Box sx={{ mt: 2 }}>
        <Stack
          sx={{
            overflowX: { xs: "auto" },
            overflowY: { xs: "visible", md: "unset" },
            padding: "5px 0",
          }}
          direction={"row"}
          alignItems="center"
          gap={1}
          mt={2}
          width={"100%"}
          flexWrap="wrap"
          divider={
            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderStyle: "dashed" }}
            />
          }
        >
          <Stack sx={{ minWidth: "200px", flex: 1 }}>
            <TextField
              label="Email"
              value={filters?.email}
              onChange={(e) => {
                setFilters((prev) => ({
                  ...prev,
                  email: e.target.value,
                }));
              }}
              size="small"
            //   sx={{
            //     "& .MuiInputBase-root": {
            //       height: "56px",
            //     },
            //   }}
            />
          </Stack>
          <Stack sx={{ minWidth: "200px", flex: 1 }}>
            <TextField
              label="IP Address"
              value={filters?.ip_address}
              onChange={(e) => {
                setFilters((prev) => ({
                  ...prev,
                  ip_address: e.target.value,
                }));
              }}
              size="small"
            //   sx={{
            //     "& .MuiInputBase-root": {
            //       height: "56px",
            //     },
            //   }}
            />
          </Stack>
          <Stack sx={{ minWidth: "180px", flex: 1 }}>
            <FilterDropdown
              name="device_type"
              type="text"
              label="Device Type"
              options={deviceTypeOptions}
              onChange={(e) => {
                const selected = deviceTypeOptions.find(
                  (opt) => opt.key === e.target.value
                );
                setFilters((prev) => ({
                  ...prev,
                  device_type: selected || null,
                }));
              }}
              value={filters?.device_type?.key || ""}
              size={"small"}
              render={(deviceType, i) => (
                <MenuItem key={deviceType.key} value={deviceType.key}>
                  {deviceType.name}
                </MenuItem>
              )}
              sx={{
                "& .MuiInputBase-root": {
                //   height: "56px",
                  fontSize: "0.875rem",
                },
                "& .MuiOutlinedInput-input": {
                  padding: "8px",
                },
              }}
            />
          </Stack>
          <Stack sx={{ minWidth: "220px", flex: 1 }}>
            <FilterDropdown
              name="status"
              type="text"
              label="Status"
              options={statusOptions}
              onChange={(e) => {
                const selected = statusOptions.find(
                  (opt) => opt.key === e.target.value
                );
                setFilters((prev) => ({
                  ...prev,
                  status: selected || null,
                }));
              }}
              value={filters?.status?.key || ""}
              size={"small"}
              render={(status, i) => (
                <MenuItem key={status.key} value={status.key}>
                  {status.name}
                </MenuItem>
              )}
              sx={{
                "& .MuiInputBase-root": {
                //   height: "56px",
                  fontSize: "0.875rem",
                },
                "& .MuiOutlinedInput-input": {
                  padding: "8px",
                },
              }}
            />
          </Stack>
          <Stack>
            <ButtonResetFilter onClick={handleClearFilters} />
          </Stack>
        </Stack>
        <Box sx={{ mt: 3 }}>
          <KycReportTable
            kycList={kycData}
            filters={paginationFilter}
            otherFilters={filters}
            setFilters={setPaginationFilter}
            fetchKycLoading={loading}
            client={client}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default KycReport;

